<template>
  <div class="h-[calc(100vh-178px)] flex gap-x-4" v-if="loaded">
    <ContentBlock class="basis-60" title="Filters">
      <div class="p-4 flex flex-col gap-y-4">
        <template v-if="movementsMode">
          <SingleSelect label="Day of Week" v-model="proxyFilter.dayOfWeek" :options="daysOfWeek"
            placeholder="Select Day of Week" inline />
        </template>
        <template v-else>
          <SingleSelect label="Quarter" v-model="proxyFilter.quarter" :options="quarters"
            placeholder="Select Quarter" inline />
          <SingleSelect label="Vehicle Type" v-model="proxyFilter.vehicleType" :options="vehicleTypes"
            placeholder="Select Vehicle Type" inline />
        </template>
        <div
          class="mt-8 flex text-zinc-50 space-x-2 items-center justify-center px-3 py-2 rounded-lg border border-accent bg-accent/75 hover:bg-accent text-sm cursor-pointer"
          @click="applyFilter">
          <span>Apply</span>
        </div>
      </div>
    </ContentBlock>
    <div class="grow rounded-lg overflow-hidden h-full relative" ref="mapElement">
      <div v-if="processing"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-y-3 bg-zinc-900/60 text-zinc-50">
        <IconSpinner class="animate-spin w-10 h-10" />
        <span class="text-sm">{{ processingMessage }}</span>
      </div>
      <div class="absolute bottom-4 right-4 w-28 bg-zinc-100 dark:bg-zinc-800 rounded-md shadow">
        <div class="rounded-t-md bg-zinc-200 dark:bg-zinc-700 p-2 flex justify-between items-center">
          <h5 class="font-medium text-sm">Legend</h5>
        </div>
        <div class="p-2 text-sm">
          <ul>
            <li class="flex items-center justify-between gap-x-2" v-for="(threshold, color) in legendColors" :key="color">
              <div class="h-3.5 w-3.5 rounded-full shrink-0" :style="`background-color: ${color}`"></div>
              <div>{{ formatNumber(+threshold) }}+</div>
            </li>
          </ul>
        </div>
      </div>
      <!-- legacy tile details -->
      <div
        class="bg-zinc-100 dark:bg-zinc-800 px-4 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-md absolute right-4 top-4 min-w-64"
        v-show="!movementsMode && showFocusedZoneDetails">
        <div class="col-span-2 text-center text-lg font-semibold mb-1">{{ get(focusedZone,
          `properties.${config.zonePropertyMap?.name || config.zonePropertyMap?.id}`, "id") }}</div>
        <template v-for="(value, key) in focusedZone?.properties" :key="key">
          <template v-if="key !== config.zonePropertyMap?.name && key !== config.zonePropertyMap?.id">
            <div class="flex justify-between items-center" :class="key === 'total' ? 'mt-1 pt-1 border-t border-zinc-200 dark:border-zinc-700' : ''">
              <div class="capitalize">{{ key }}</div>
              <div class="text-right">{{ +value }}</div>
            </div>
          </template>
        </template>
      </div>
      <!-- turning movement details -->
      <div v-if="movementsMode && focusedIntersection"
        class="bg-zinc-100 dark:bg-zinc-800 px-4 py-3 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-md absolute right-4 top-4 w-80 text-sm max-h-[calc(100%-2rem)] overflow-y-auto">
        <div class="flex items-start gap-x-2 mb-1">
          <div>
            <div class="font-semibold leading-tight">{{ focusedIntersection.name }}</div>
            <div class="text-xs text-zinc-500 dark:text-zinc-400">
              {{ focusedIntersection.dayLabel }}
              <span v-if="focusedIntersection.routes && !focusedIntersection.name.includes(focusedIntersection.routes)"> · {{ focusedIntersection.routes }}</span>
            </div>
          </div>
          <button v-if="focusedIntersectionPinned" type="button"
            class="ml-auto text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 size-4" aria-label="Close"
            @click="clearFocusedIntersection">
            <IconClose class="size-4" />
          </button>
        </div>
        <div v-if="focusedIntersection.summary.total > 0" class="flex justify-center">
          <TurningMovementDiagram :summary="focusedIntersection.summary" :size="288" />
        </div>
        <div v-else class="py-6 text-center text-zinc-500 dark:text-zinc-400">No turning movement counts</div>
        <table v-if="focusedIntersection.summary.total > 0" class="w-full mt-2 text-xs tabular-nums">
          <thead>
            <tr class="text-zinc-500 dark:text-zinc-400">
              <th class="text-left font-medium py-0.5">From \ To</th>
              <th v-for="to in focusedIntersection.legs" :key="to" class="text-right font-medium py-0.5">{{ DIRECTION_LABELS[to] }}</th>
              <th class="text-right font-medium py-0.5">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="from in focusedIntersection.legs" :key="from" class="border-t border-zinc-200 dark:border-zinc-700">
              <td class="py-0.5">{{ DIRECTION_LABELS[from] }}</td>
              <td v-for="to in focusedIntersection.legs" :key="to" class="text-right py-0.5"
                :class="{ 'text-zinc-400 dark:text-zinc-500': movementCount(from, to) === null }">
                {{ formatNumber(movementCount(from, to)) }}
              </td>
              <td class="text-right py-0.5 font-semibold">{{ formatNumber(get(focusedIntersection.summary.approaches, [from, "total"], 0)) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  <div class="h-[calc(100vh-178px)] flex place-items-center justify-center" v-else>
    <IconSpinner class="animate-spin w-12 h-12" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, nextTick } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useAuth0 } from "@auth0/auth0-vue";
import { isEmpty, isPlainObject, isString, cloneDeep, first, toLower, uniq, get } from "lodash";
import { scaleThreshold } from "d3-scale";
import { schemeGreens } from "d3-scale-chromatic";
import { bbox } from "@turf/turf";
import AirSageMap from "@/lib/map";
import {
  buildIntersectionGeoJSON,
  DIRECTIONS,
  DIRECTION_LABELS,
  DAY_PROPERTY,
  INTERSECTION_ID_PROPERTY,
  TOTAL_PROPERTY
} from "@/lib/intersections/movements";
import ContentBlock from "@/components/ContentBlock.vue";
import SingleSelect from "@/components/SingleSelect.vue";
import IconSpinner from "@/components/icons/IconSpinner.vue";
import IconClose from "@/components/icons/IconClose.vue";
import TurningMovementDiagram from "@/components/TurningMovementDiagram.vue";
import { StudiesService } from "@/services";
import { useStudyStore } from "@/stores/study";
import { useAlertStore } from "@/stores/alert";

const alertStore = useAlertStore();
const studyStore = useStudyStore();
const route = useRoute();
const { getAccessTokenSilently } = useAuth0();
const { config } = storeToRefs(studyStore);

// legacy vector tile source, used when no browser-side inputs are published for the study
const POINT_LAYER_ID = "points";
// browser-side turning movement source/layers
const MOVEMENTS_SOURCE_ID = "ia-intersections";
const MOVEMENTS_LAYERS = {
  circles: "ia-intersections-circles",
  halo: "ia-intersections-halo",
  highlight: "ia-intersections-highlight"
};
const NO_INTERSECTION_ID = "";
const WEEKDAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const loaded = ref(false);
// decided once the inputs request settles: "movements" (browser-side) or "tiles" (legacy)
const mode = ref(null);
const movementsMode = computed(() => mode.value === "movements");
const processing = ref(false);
const processingMessage = ref("");
// legacy tile studies list the quarters to switch between; schema configs store date intervals instead
const quarters = computed(() => (config.value.dates || []).filter(isString));
const vehicleTypes = computed(() => config.value.vehicleTypes || []);
/**
 * Day buckets per the study config schema: a weekday -> bucket label map (listed Monday first).
 * Legacy tile studies may store a list of labels instead.
 */
const daysOfWeek = computed(() => {
  const buckets = config.value.weekdayBuckets || [];
  const labels = isPlainObject(buckets) ? uniq(WEEKDAY_ORDER.map((day) => buckets[day])) : buckets;
  return labels.filter((label) => !isEmpty(label) && toLower(label) !== "omit");
});
const dayLabel = (dow) => daysOfWeek.value.find((d) => toLower(d) === dow) || dow;

let data;
const defaultFilter = () => ({ vehicleType: "", quarter: "", dayOfWeek: "" });
const proxyFilter = ref(defaultFilter());
const filter = ref(defaultFilter());
let hoverStateId;
const focusedZone = ref(null);
const showFocusedZoneDetails = ref(false);
const legendColors = ref([]);

const focusedIntersection = ref(null);
const focusedIntersectionPinned = ref(false);
const hoveredIntersectionId = ref(null);
const highlightedIntersectionId = computed(() => {
  if (hoveredIntersectionId.value !== null) {
    return hoveredIntersectionId.value;
  }
  return focusedIntersectionPinned.value && focusedIntersection.value ? focusedIntersection.value.id : null;
});

let map = null;
let inputsPromise = null;
let summaries = new Map();
let featureProps = new Map();

const formatNumber = (value) => (Number.isFinite(value) ? value.toLocaleString() : "-");

/** Count for a From \ To cell; the delivered data does not count U-turns, so an empty diagonal cell is shown as "—" rather than 0. */
const movementCount = (from, to) => {
  const count = get(focusedIntersection.value.summary.matrix, [from, to], 0);
  return from === to && count === 0 ? null : count;
};

/**
 * Fetch the intersection GeoJSON and movements CSV uploaded to the study's
 * materialized-views folder (proxied by the API). Resolves to null when the study
 * has none, so the view can fall back to the legacy vector tiles.
 */
async function loadInputs(id) {
  const token = await getAccessTokenSilently();
  const options = (responseType) => ({
    headers: { Authorization: `Bearer ${token}` },
    responseType
  });
  try {
    const [intersections, csvText] = await Promise.all([
      StudiesService.getInput(id, "intersections", options("json")),
      StudiesService.getInput(id, "movements", options("text"))
    ]);
    return { intersections, csvText };
  } catch (error) {
    if ([400, 404].includes(get(error, "response.status"))) {
      return null;
    }
    throw error;
  }
}

watch(config, async () => {
  if (!isEmpty(config.value)) {
    const { id } = route.params;
    // start downloading while the map initializes
    inputsPromise = loadInputs(id).catch((error) => {
      alertStore.alertError(error);
      return null;
    });
    loaded.value = true;
    await nextTick();
    proxyFilter.value.vehicleType = first(vehicleTypes.value);
    proxyFilter.value.quarter = first(quarters.value);
    proxyFilter.value.dayOfWeek = first(daysOfWeek.value);
    applyFilter();
    loadMap();
  }
});

onBeforeUnmount(studyStore.reset);

function applyFilter() {
  const proxy = cloneDeep(proxyFilter.value);
  filter.value = { ...proxy, dayOfWeek: toLower(proxy.dayOfWeek) };
}

async function readMaterializedView(view) {
  try {
    const { id } = route.params;
    const token = await getAccessTokenSilently();
    return await StudiesService.getMaterializedView(id, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: { view }
    });
  } catch (error) {
    alertStore.alertError(error);
  }
}

function generateExpression (thresholds, tileKey) {
  const arrayToObject = (array) => {
    const legend = {};
    array.unshift(0);
    while (array.length) {
      const color = array.shift();
      const threshold = array.shift();
      legend[threshold] = color;
    }
    return legend;
  };
  const colors = schemeGreens[thresholds.length + 1];
  const scale = scaleThreshold(thresholds, colors);
  let expression = [];
  expression.push(scale(0))
  scale.domain().forEach((d) => {
    if (!expression.includes(scale(d))) {
      expression.push(d, scale(d));
    }
  });
  expression = ["step", ["to-number", ["get", tileKey]]].concat(expression);
  legendColors.value = arrayToObject(expression.slice(2));
  return expression;
}

// ---------------------------------------------------------------------------
// legacy vector tile mode
// ---------------------------------------------------------------------------

function tileLayerName(vehicle, quarter) {
  return `${POINT_LAYER_ID}-${toLower(vehicle)}${toLower(quarter.replace("-", ""))}`;
}

function applyTileFilter() {
  quarters.value.forEach((quarter) => {
    vehicleTypes.value.forEach((vehicle) => {
      const layer = tileLayerName(vehicle, quarter);
      if (map.getLayer(layer)) {
        map.setLayoutProperty(layer, "visibility", "none");
      }
    });
  });
  const layer = tileLayerName(filter.value.vehicleType, filter.value.quarter);
  if (map.getLayer(layer)) {
    map.setLayoutProperty(layer, "visibility", "visible");
  }
}

function addTileLayers() {
  const { id } = route.params;
  map.addSource(POINT_LAYER_ID, {
    type: "vector",
    tiles: [`${import.meta.env.VITE_TILE_CDN_URL}/${id}/{z}/{x}/{y}.pbf`],
    promoteId: "name",
    minzoom: 0,
    maxzoom: 8
  });

  const expression = generateExpression(data.thresholds.map((th) => +th), "total");
  quarters.value.forEach((quarter) => {
    vehicleTypes.value.forEach((vehicle) => {
      const sourceLayer = `${toLower(vehicle)}${toLower(quarter.replace("-", ""))}`;
      const layer = `${POINT_LAYER_ID}-${sourceLayer}`;
      map.addLayer({
        id: layer,
        type: "circle",
        source: POINT_LAYER_ID,
        "source-layer": sourceLayer,
        paint: {
          "circle-color": expression,
          "circle-radius": 10,
          "circle-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 1, 0.8]
        }
      });

      map.setLayoutProperty(layer, "visibility", "none");

      map.on("mousemove", layer, (e) => {
        const [feature] = e.features;
        if (feature) {
          const { id } = feature;
          if (id) {
            map.getCanvas().style.cursor = "pointer";
            if (hoverStateId) {
              map.setFeatureState({
                source: POINT_LAYER_ID,
                sourceLayer,
                id: hoverStateId
              }, { hover: false });
            }
            hoverStateId = id;
            map.setFeatureState({
              source: POINT_LAYER_ID,
              sourceLayer,
              id: hoverStateId
            }, { hover: true });

            focusedZone.value = feature;
            showFocusedZoneDetails.value = true;
          }
        }
      });

      map.on("mouseleave", layer, () => {
        map.getCanvas().style.cursor = "";
        if (hoverStateId) {
          map.setFeatureState({
            source: POINT_LAYER_ID,
            sourceLayer,
            id: hoverStateId
          }, { hover: false });
        }
        hoverStateId = null;

        focusedZone.value = null;
        showFocusedZoneDetails.value = false;
      });
    });
  });

  map.setLayoutProperty(tileLayerName(filter.value.vehicleType, filter.value.quarter), "visibility", "visible");
  map.centerOn(config.value.bbox, { easing: () => 1 });
}

// ---------------------------------------------------------------------------
// browser-side turning movements mode
// ---------------------------------------------------------------------------

function movementsFilter() {
  return ["==", ["get", DAY_PROPERTY], filter.value.dayOfWeek];
}

function movementsHighlightFilter() {
  return ["all", movementsFilter(), ["==", ["get", INTERSECTION_ID_PROPERTY], highlightedIntersectionId.value ?? NO_INTERSECTION_ID]];
}

function applyHighlightFilter() {
  map.setFilter(MOVEMENTS_LAYERS.halo, movementsHighlightFilter());
  map.setFilter(MOVEMENTS_LAYERS.highlight, movementsHighlightFilter());
}

function applyMovementsFilter() {
  if (!map.getLayer(MOVEMENTS_LAYERS.circles)) {
    return;
  }
  map.setFilter(MOVEMENTS_LAYERS.circles, movementsFilter());
  applyHighlightFilter();
  if (focusedIntersection.value) {
    focusedIntersection.value = describeIntersection(focusedIntersection.value.id);
  }
}

watch(highlightedIntersectionId, () => {
  if (map && map.getLayer(MOVEMENTS_LAYERS.halo)) {
    applyHighlightFilter();
  }
});

function describeIntersection(intersectionId) {
  const props = featureProps.get(intersectionId) || {};
  const summary = summaries.get(intersectionId)?.get(filter.value.dayOfWeek) || { total: 0, approaches: {}, matrix: {} };
  return {
    id: intersectionId,
    name: props.name || `Intersection ${intersectionId}`,
    routes: props.routes || "",
    dayLabel: dayLabel(filter.value.dayOfWeek),
    summary,
    legs: DIRECTIONS.filter((direction) => summary.approaches[direction] || Object.values(summary.matrix).some((row) => row[direction]))
  };
}

function focusIntersection(intersectionId, pin) {
  if (focusedIntersectionPinned.value && !pin) {
    return;
  }
  focusedIntersection.value = describeIntersection(intersectionId);
  if (pin) {
    focusedIntersectionPinned.value = true;
  }
}

function clearFocusedIntersection() {
  focusedIntersection.value = null;
  focusedIntersectionPinned.value = false;
}

function addMovementsLayers(geojson, linkStats) {
  const thresholds = uniq([linkStats.total.p25, linkStats.total.p50, linkStats.total.p75].filter((t) => t > 0));
  let colorExpression = schemeGreens[3][2];
  if (thresholds.length) {
    colorExpression = generateExpression(thresholds, TOTAL_PROPERTY);
  } else {
    legendColors.value = { 0: colorExpression };
  }
  const radius = ["interpolate", ["linear"], ["sqrt", ["to-number", ["get", TOTAL_PROPERTY], 0]], 0, 7, Math.sqrt(Math.max(linkStats.total.max, 1)), 22];

  map.addSource(MOVEMENTS_SOURCE_ID, { type: "geojson", data: geojson });
  const circlePaint = {
    "circle-color": colorExpression,
    "circle-radius": radius,
    "circle-opacity": 0.9,
    "circle-stroke-width": 1,
    "circle-stroke-color": "rgba(0, 0, 0, 0.4)"
  };
  map.addLayer({
    id: MOVEMENTS_LAYERS.circles,
    type: "circle",
    source: MOVEMENTS_SOURCE_ID,
    filter: movementsFilter(),
    paint: circlePaint
  });
  // the highlighted intersection is redrawn above its neighbours so the halo is never covered
  map.addLayer({
    id: MOVEMENTS_LAYERS.halo,
    type: "circle",
    source: MOVEMENTS_SOURCE_ID,
    filter: movementsHighlightFilter(),
    paint: {
      "circle-color": "rgba(255, 255, 255, 0.9)",
      "circle-radius": ["+", radius, 4]
    }
  });
  map.addLayer({
    id: MOVEMENTS_LAYERS.highlight,
    type: "circle",
    source: MOVEMENTS_SOURCE_ID,
    filter: movementsHighlightFilter(),
    paint: circlePaint
  });

  const featureAt = (point) => map.queryRenderedFeatures(point, { layers: [MOVEMENTS_LAYERS.circles] })[0] || null;

  map.on("mousemove", (e) => {
    const feature = featureAt(e.point);
    map.getCanvas().style.cursor = feature ? "pointer" : "";
    hoveredIntersectionId.value = feature ? feature.properties[INTERSECTION_ID_PROPERTY] : null;
    if (feature) {
      focusIntersection(feature.properties[INTERSECTION_ID_PROPERTY], false);
    } else if (!focusedIntersectionPinned.value) {
      focusedIntersection.value = null;
    }
  });
  map.on("mouseout", () => {
    map.getCanvas().style.cursor = "";
    hoveredIntersectionId.value = null;
    if (!focusedIntersectionPinned.value) {
      focusedIntersection.value = null;
    }
  });
  map.on("click", (e) => {
    const feature = featureAt(e.point);
    if (feature) {
      focusIntersection(feature.properties[INTERSECTION_ID_PROPERTY], true);
    } else {
      clearFocusedIntersection();
    }
  });
}

// ---------------------------------------------------------------------------

watch(filter, () => {
  if (!map || !mode.value) {
    return;
  }
  if (movementsMode.value) {
    applyMovementsFilter();
  } else {
    applyTileFilter();
  }
}, { deep: true });

async function loadLayers() {
  processing.value = true;
  processingMessage.value = "Loading turning movement counts…";
  const inputs = await inputsPromise;
  if (!map) {
    return;
  }

  if (inputs) {
    mode.value = "movements";
    const result = buildIntersectionGeoJSON(inputs.intersections, inputs.csvText, { dayBuckets: daysOfWeek.value });
    console.debug("turning movements processed", result.stats);
    summaries = result.summaries;
    featureProps = new Map(inputs.intersections.features.map((f) => {
      const props = f.properties;
      return [String(props[INTERSECTION_ID_PROPERTY]), props];
    }));
    addMovementsLayers(result.geojson, result.stats);
    if (inputs.intersections.features.length) {
      // leave room on the right for the details panel
      map.centerOn(bbox(inputs.intersections), { easing: () => 1, padding: { top: 60, bottom: 60, left: 60, right: 380 } });
    } else if (config.value.bbox) {
      map.centerOn(config.value.bbox, { easing: () => 1 });
    }
  } else {
    mode.value = "tiles";
    data = await readMaterializedView("materialized_data");
    if (!map) {
      return;
    }
    addTileLayers();
  }
  processing.value = false;
}

const mapElement = ref(null);
function loadMap() {
  map = new AirSageMap(mapElement.value, {
    eventHandlers: {
      "style.load": loadLayers
    }
  });
}
</script>
